"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { MapPin,
  Clock,
  Calendar,
  Compass,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  ThermometerSun,
  Droplets,
  Snowflake,
  Info,
  Navigation,
  ArrowRight,
  Tag,
  Star,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { decodeHtml, cleanHeaderTags } from "@/_helper/helper";

interface AttractionDetailClientProps {
  attractionsData: any;
}

export default function AttractionDetailClient({
  attractionsData,
}: AttractionDetailClientProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Lightbox State
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [activeLightboxIndex, setActiveLightboxIndex] = useState(0);

  const attraction = attractionsData;
  const sliderImages = attraction
    ? [{ ID: 0, Image: attraction.BannerImage }, ...(attraction.Images || [])]
    : [];

  // All images for lightbox (Banner + Gallery)
  const allImages = attraction
    ? attraction.Images?.map((img: any) => img.Image)
    : [];

  // Handle Keyboard for Lightbox
  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isLightboxOpen) return;
      if (e.key === "ArrowRight") nextLightbox();
      if (e.key === "ArrowLeft") prevLightbox();
      if (e.key === "Escape") setIsLightboxOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "unset";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isLightboxOpen, activeLightboxIndex]);

  const nextImage = () =>
    setCurrentImageIndex((prev) => (prev + 1) % sliderImages.length || 0);
  const prevImage = () =>
    setCurrentImageIndex(
      (prev) => (prev - 1 + sliderImages.length) % sliderImages.length || 0,
    );

  const nextLightbox = () =>
    setActiveLightboxIndex((prev) => (prev + 1) % allImages.length);
  const prevLightbox = () =>
    setActiveLightboxIndex(
      (prev) => (prev - 1 + allImages.length) % allImages.length,
    );

  if (!attraction) {
    return (
      <div className="min-h-screen pt-40 pb-20 text-center flex flex-col items-center justify-center bg-gray-50">
        <div className="p-10 bg-white rounded-xl shadow-md max-w-lg mx-auto border border-gray-100 font-sans">
          <Info className="w-12 h-12 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Attraction Not Found
          </h2>
          <p className="text-gray-500 mb-6 text-sm">
            We couldn't find the requested attraction details.
          </p>
          <Link
            href="/attractions"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full font-bold shadow-lg hover:shadow-orange-200 transition-all text-sm"
          >
            Browse All Attractions
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  const quickStats = [
    {
      icon: MapPin,
      label: "Location",
      value: attraction.Distance,
      gradient: "from-orange-500 to-amber-500",
    },
    {
      icon: Clock,
      label: "Duration",
      value: attraction.Duration,
      gradient: "from-green-500 to-emerald-600",
    },
    {
      icon: Calendar,
      label: "Best Time",
      value: attraction.BestTime,
      gradient: "from-blue-500 to-indigo-600",
    },
    {
      icon: Compass,
      label: "Travel Time",
      value: attraction.TravelTime,
      gradient: "from-purple-500 to-violet-600",
    },
  ];

  return (
    <main className="bg-white text-gray-900 font-sans pb-20">
      {/* 1. Hero Section - Standard Banner Height */}
      <section className="relative h-[40vh] md:h-[55vh] overflow-hidden group">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImageIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0"
          >
            <Image
              src={sliderImages[currentImageIndex]?.Image || "/placeholder.jpg"}
              alt={`${attraction.Title} - View ${currentImageIndex + 1}`}
              fill
              priority
              className="object-cover cursor-pointer"
              onClick={() => {
                setActiveLightboxIndex(currentImageIndex);
                setIsLightboxOpen(true);
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 via-gray-900/0 to-gray-900/0" />
          </motion.div>
        </AnimatePresence>

        {/* Hero Controls */}
        <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4 md:px-8 pointer-events-none z-30">
          <button
            onClick={prevImage}
            className="pointer-events-auto w-10 h-10 bg-black/20 hover:bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center transition-all group/btn"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={nextImage}
            className="pointer-events-auto w-10 h-10 bg-black/20 hover:bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center transition-all group/btn"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Hero Bottom Content */}
        <div className="absolute bottom-10 left-0 right-0 z-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-1.5 rounded-full mb-3 shadow-lg">
                <Tag className="w-3.5 h-3.5" />
                <span className="font-bold tracking-wider uppercase text-[10px]">
                  {attraction.AttractionCategory}
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-2 tracking-tight">
                {attraction.Title}
              </h1>
              <p className="text-white/80 text-base md:text-lg font-light italic max-w-3xl">
                {attraction.TagLine}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. Quick Action Stats Row */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-40 -mt-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {quickStats.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white rounded-xl p-4 shadow-xl border border-gray-100/50 flex flex-col md:flex-row items-center gap-4 group"
            >
              <div
                className={`w-10 h-10 rounded-lg bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform`}
              >
                <item.icon size={20} />
              </div>
              <div className="text-center md:text-left">
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest leading-none mb-1">
                  {item.label}
                </p>
                <p className="font-bold text-gray-900 text-sm xl:text-base">
                  {item.value || "N/A"}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-16 space-y-16">
        {/* 3. About Section & Main Data */}
        <section className="space-y-6">
          <h2 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
            <span className="w-1.5 h-8 bg-orange-500 rounded-full"></span>
            About the Attraction
          </h2>
          <div
            className="text-sm md:text-base text-gray-600 leading-relaxed max-w-5xl attraction-content blog-content"
            dangerouslySetInnerHTML={{
              __html: cleanHeaderTags(decodeHtml(attraction.Description)),
            }}
          />

          {/* Gallery Simple Grid */}
          {attraction.Images?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
              {attraction.Images.map((image: any, idx: number) => (
                <motion.div
                  key={idx}
                  whileHover={{ scale: 1.02 }}
                  className="relative aspect-video rounded-xl overflow-hidden shadow-md group cursor-pointer"
                  onClick={() => {
                    setActiveLightboxIndex(idx + 1); // +1 because index 0 is BannerImage
                    setIsLightboxOpen(true);
                  }}
                >
                  <Image
                    src={image.Image}
                    alt={`${attraction.Title} Gallery ${idx + 1}`}
                    fill
                    className="object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </motion.div>
              ))}
            </div>
          )}
        </section>

        {/* 4. Experiences / Why Visit */}
        {attraction.WhyVisit?.length > 0 && (
          <section className="space-y-8">
            <h2 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
              <span className="w-1.5 h-8 bg-green-500 rounded-full"></span>
              Why Experience This?
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {attraction.WhyVisit.map((reason: any, index: number) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl p-6 shadow-md border border-gray-100 flex flex-col items-center text-center group hover:shadow-lg hover:border-amber-200 transition-all font-sans"
                >
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-white mb-4 shadow-md">
                    <Star size={20} fill="currentColor" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {reason.Title}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed font-sans">
                    {reason.Description}
                  </p>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* 5. Journey & Activity Guide */}
        <div className="space-y-8">
          {/* How to Reach Grid Style - Full Width */}
          {attraction.HowToReach?.length > 0 && (
            <div className="bg-gray-50/50 rounded-2xl p-6 md:p-10 border border-gray-100 group font-sans">
              <h3 className="text-xl md:text-2xl font-bold mb-8 flex items-center gap-3">
                <div className="p-2.5 bg-orange-500 text-white rounded-xl shadow-lg shadow-orange-200">
                  <Navigation size={22} />
                </div>
                How to Reach
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {attraction.HowToReach.map((reach: any, index: number) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4 hover:border-orange-200 transition-colors group/item"
                  >
                    <div className="p-2 bg-orange-50 text-orange-600 rounded-lg group-hover/item:bg-orange-600 group-hover/item:text-white transition-all">
                      <Navigation size={18} />
                    </div>
                    <div>
                      <h4 className="font-bold text-base text-gray-900 mb-1">
                        {reach.Title}
                      </h4>
                      <p className="text-xs text-gray-500 leading-relaxed font-sans">
                        {reach.Description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity Guide - Full Width */}
          {attraction.ThingsToDo?.length > 0 && (
            <div className="bg-orange-50/20 rounded-2xl p-6 md:p-10 border border-orange-100 shadow-sm font-sans">
              <h3 className="text-xl md:text-2xl font-bold mb-8 flex items-center gap-3">
                <div className="p-2.5 bg-orange-600 text-white rounded-xl shadow-lg shadow-orange-200">
                  <Check size={22} />
                </div>
                Traveler Activities
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {attraction.ThingsToDo.map((activity: any, index: number) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-4 shadow-sm border border-orange-50 flex items-center gap-3 transition-all hover:border-orange-200 hover:shadow-md"
                  >
                    <div className="w-5 h-5 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check size={12} />
                    </div>
                    <span className="text-xs text-gray-700 font-medium leading-relaxed font-sans">
                      {activity.Description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 6. Practical Planning (Season & Advice) */}
        <div className="space-y-8">
          {/* Timing Section - Full Width with Season Colors */}
          <div className="bg-white rounded-2xl p-6 md:p-10 border border-gray-100 shadow-xl shadow-gray-200/20 font-sans">
            <h3 className="text-xl md:text-2xl font-bold mb-10 flex items-center gap-3">
              <div className="p-2.5 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-200">
                <Clock size={22} />
              </div>
              Best Planning Seasons
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
              {attraction.BestTimes?.map((season: any, idx: number) => {
                const isWinter = season.SeasonType === "Winter";
                const isMonsoon =
                  season.SeasonType === "Monsoon" ||
                  season.SeasonType === "All Season";

                return (
                  <motion.div
                    key={idx}
                    whileHover={{ y: -5 }}
                    className={`flex flex-col gap-6 p-8 rounded-2xl border transition-all group ${
                      isWinter
                        ? "bg-blue-50/50 border-blue-100 hover:bg-blue-50 focus:bg-blue-50"
                        : isMonsoon
                          ? "bg-emerald-50/50 border-emerald-100 hover:bg-emerald-50 focus:bg-emerald-50"
                          : "bg-amber-50/50 border-amber-100 hover:bg-amber-50 focus:bg-amber-50"
                    }`}
                  >
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${
                        isWinter
                          ? "bg-blue-100 text-blue-600"
                          : isMonsoon
                            ? "bg-emerald-100 text-emerald-600"
                            : "bg-amber-100 text-amber-600"
                      }`}
                    >
                      {isWinter ? (
                        <Snowflake size={24} />
                      ) : isMonsoon ? (
                        <Droplets size={24} />
                      ) : (
                        <ThermometerSun size={24} />
                      )}
                    </div>
                    <div>
                      <h4
                        className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${
                          isWinter
                            ? "text-blue-500"
                            : isMonsoon
                              ? "text-emerald-500"
                              : "text-amber-500"
                        }`}
                      >
                        {season.SeasonType}
                      </h4>
                      <p className="font-bold text-gray-900 text-lg mb-2">
                        {season.Title}
                      </p>
                      <div
                        className="text-xs text-gray-500 leading-relaxed font-sans blog-content"
                        dangerouslySetInnerHTML={{
                          __html: cleanHeaderTags(decodeHtml(season.Description)),
                        }}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Dos & Donts Section - Side by Side on Desktop */}
          <div className="grid md:grid-cols-2 gap-8 font-sans">
            {attraction.Dos && (
              <div className="bg-green-50/40 rounded-2xl p-8 border border-green-100 shadow-sm flex flex-col gap-6 font-sans">
                <h4 className="font-bold text-green-700 flex items-center gap-3 text-sm uppercase tracking-widest">
                  <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                    <Check size={16} />
                  </div>
                  Essential Dos
                </h4>
                <div
                  className="text-xs md:text-sm text-green-800 leading-relaxed font-medium dos-content blog-content"
                  dangerouslySetInnerHTML={{
                    __html: cleanHeaderTags(decodeHtml(attraction.Dos)),
                  }}
                />
              </div>
            )}
            {attraction.Donts && (
              <div className="bg-red-50/40 rounded-2xl p-8 border border-red-100 shadow-sm flex flex-col gap-6 font-sans">
                <h4 className="font-bold text-red-700 flex items-center gap-3 text-sm uppercase tracking-widest">
                  <div className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center">
                    <X size={16} />
                  </div>
                  Important Donts
                </h4>
                <div
                  className="text-xs md:text-sm text-red-800 leading-relaxed font-medium donts-content blog-content"
                  dangerouslySetInnerHTML={{
                    __html: cleanHeaderTags(decodeHtml(attraction.Donts)),
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* 7. Nearby Attractions */}
        {attraction.Nearby?.length > 0 && (
          <section className="space-y-8 pt-10 border-t border-gray-100 font-sans">
            <div className="text-center font-sans">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                Explore More <span className="text-orange-600">Nearby</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 font-sans">
              {attraction.Nearby.map((nearby: any, idx: number) => (
                <Link
                  key={idx}
                  href={`/attractions/${nearby.AttractionURL}/`}
                  className="group block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all border border-gray-200 font-sans"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <Image
                      src={nearby.MainImage || "/placeholder.jpg"}
                      alt={nearby.Title}
                      fill
                      className="object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur px-2 py-1 rounded text-[10px] font-bold text-white flex items-center gap-1">
                      <Navigation size={10} />
                      {nearby.KmAwayFromMainAttraction} KM
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-bold text-xs text-gray-900 group-hover:text-orange-600 transition-colors truncate">
                      {nearby.Title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* 8. Booking CTA Banner */}
        {/* {lookUpCMSData?.["Attractions - Detail  page CTA"] && (
          <section className="mt-16 font-sans">
            <div className="relative bg-gradient-to-r from-orange-600 to-amber-600 rounded-2xl p-8 md:p-12 text-white shadow-xl text-center overflow-hidden">
              <div className="absolute top-0 right-0 opacity-10 pointer-events-none">
                <Ticket className="w-48 h-48 -mr-10 -mt-10 rotate-15" />
              </div>
              <div
                className="max-w-3xl mx-auto space-y-4 cta-theme relative z-10 font-sans"
                dangerouslySetInnerHTML={{
                  __html: cleanHeaderTags(decodeHtml(
                    lookUpCMSData?.["Attractions - Detail  page CTA"]?.Template,
                  )),
                }}
              />
              <div className="flex flex-wrap items-center justify-center gap-4 mt-8 relative z-10">
                <Link
                  href="/sou-tickets"
                  className="px-8 py-3 bg-white text-orange-600 rounded-lg font-bold shadow-lg hover:shadow-orange-200 transition-all flex items-center gap-2 group/btn"
                >
                  Book Instant Tickets
                  <ArrowRight
                    size={18}
                    className="group-hover/btn:translate-x-1 transition-transform"
                  />
                </Link>
                <Link
                  href="/contact"
                  className="px-8 py-3 border-2 border-white/30 text-white rounded-lg font-bold hover:bg-white/10 transition-all font-sans"
                >
                  Inquire Now
                </Link>
              </div>
            </div>
          </section>
        )} */}
      </div>

      {/* 9. Lightbox Modal */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999999] bg-black/90 flex flex-col items-center justify-center backdrop-blur-xl"
          >
            {/* Close Button */}
            <button
              onClick={() => setIsLightboxOpen(false)}
              className="absolute top-6 right-6 z-10 p-3 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all cursor-pointer"
            >
              <X size={28} />
            </button>

            {/* Main Image Slider */}
            <div className="relative w-full flex-grow flex items-center justify-center px-4">
              <div className="relative w-full max-w-6xl aspect-video">
                <Image
                  src={allImages[activeLightboxIndex] || "/placeholder.jpg"}
                  alt={`Image ${activeLightboxIndex + 1}`}
                  fill
                  className="object-contain"
                  priority
                />
              </div>

              {/* Navigation Arrows - Screen Sides */}
              <button
                onClick={prevLightbox}
                className="fixed left-4 md:left-10 top-1/2 -translate-y-1/2 p-4 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-all z-[120] cursor-pointer"
              >
                <ChevronLeft size={25} />
              </button>
              <button
                onClick={nextLightbox}
                className="fixed right-4 md:right-10 top-1/2 -translate-y-1/2 p-4 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-all z-[120] cursor-pointer"
              >
                <ChevronRight size={25} />
              </button>
            </div>

            {/* Thumbnail Slider */}
            <div className="w-full bg-black/50 py-6 mt-auto">
              <div className="container mx-auto px-4 overflow-x-auto scrollbar-hide flex items-center justify-center gap-3 p-1">
                {allImages.map((img: any, idx: number) => (
                  <button
                    key={idx}
                    onClick={() => setActiveLightboxIndex(idx)}
                    className={`relative flex-shrink-0 w-20 h-14 md:w-28 md:h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      activeLightboxIndex === idx
                        ? "border-orange-500 scale-105 shadow-lg shadow-orange-500/20"
                        : "border-transparent opacity-50 hover:opacity-100"
                    }`}
                  >
                    <Image
                      src={img}
                      alt={`Thumb ${idx + 1}`}
                      fill
                      className="object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
